import {
  Button,
  Collapse,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useMediaQuery, useTheme } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { trackPostHogEvent } from "src/analytics/posthog";
import AvailabilitySelector from "src/components/availability/availability";
import IncrementorButton from "src/components/buttons/incrementorbutton";
import MainButton from "src/components/buttons/mainbutton";
import SecondaryButton from "src/components/buttons/secondbutton";
import { Availability } from "src/model_types/commonTypes.types";
import { textStyle } from "src/styles/textstyle";

type HomesSearchData = {
  location?: string;
  bedrooms?: number;
  bathrooms?: number;
  availability?: Availability;
};

const emptySearchData: HomesSearchData = {};

const sanitizeSearchData = (searchData: HomesSearchData): HomesSearchData => {
  const sanitized: HomesSearchData = {};
  const location = String(searchData.location || "").trim();

  if (location) {
    sanitized.location = location;
  }

  if (Number(searchData.bedrooms || 0) > 0) {
    sanitized.bedrooms = Number(searchData.bedrooms);
  }

  if (Number(searchData.bathrooms || 0) > 0) {
    sanitized.bathrooms = Number(searchData.bathrooms);
  }

  const availability = searchData.availability;
  if (availability?.type === "anytime") {
    sanitized.availability = { type: "anytime" };
  }

  if (availability?.type === "selected_days") {
    const hasSelectedDays = Array.isArray(availability.days)
      && availability.days.length > 0;
    if (hasSelectedDays) {
      sanitized.availability = {
        type: "selected_days",
        days: availability.days,
      };
    }
  }

  if (availability?.type === "student") {
    const student = availability.student;
    const hasStudentToggle = Boolean(
      student?.fallAndWinter || student?.springAndSummer || student?.fullYear,
    );

    if (hasStudentToggle) {
      sanitized.availability = {
        type: "student",
        student,
      };
    }
  }

  return sanitized;
};

type FindHomesSearchBoxProps = {
  mode?: "modal" | "inline";
  source?: "find_homes_modal" | "landing_search_box";
  onCancel?: () => void;
  onSubmitted?: () => void;
};

export default function FindHomesSearchBox({
  mode = "inline",
  source = "landing_search_box",
  onCancel,
  onSubmitted,
}: FindHomesSearchBoxProps) {
  const [collapseOpen, setCollapseOpen] = useState(false);
  const [searchData, setSearchData] = useState<HomesSearchData>(emptySearchData);
  const navigate = useNavigate();
  const mobileTheme = useTheme();
  const isDesktop = useMediaQuery(mobileTheme.breakpoints.down("sm"));
  const normalizedSearchData = sanitizeSearchData(searchData);
  const activeFiltersCount = Object.keys(normalizedSearchData).length;

  const clearFilters = () => {
    setSearchData(emptySearchData);
  };

  const clearAvailability = () => {
    setSearchData({
      ...searchData,
      availability: undefined,
    });
  };

  const queryData = () => {
    const hasLocationFilter = Boolean(String(normalizedSearchData.location || "").trim());
    const hasBedroomsFilter = Number(normalizedSearchData.bedrooms || 0) > 0;
    const hasBathroomsFilter = Number(normalizedSearchData.bathrooms || 0) > 0;
    const hasAvailabilityFilter = Boolean(normalizedSearchData.availability?.type);
    const filterCount = [
      hasLocationFilter,
      hasBedroomsFilter,
      hasBathroomsFilter,
      hasAvailabilityFilter,
    ].filter(Boolean).length;

    trackPostHogEvent("homes_search_submitted", {
      has_search_filters: filterCount > 0,
      filter_count: filterCount,
      has_availability_filter: hasAvailabilityFilter,
      source,
    });

    const queryParams = new URLSearchParams();

    Object.entries({ search: normalizedSearchData }).forEach(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        queryParams.append(key, JSON.stringify(value));
      } else {
        queryParams.append(key, value as string);
      }
    });

    navigate(`/homes?${queryParams}`);
    setSearchData(emptySearchData);
    setCollapseOpen(false);
    if (onSubmitted) {
      onSubmitted();
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: mode === "modal" ? 0 : 3,
        border: mode === "modal" ? "none" : "1px solid rgba(255,255,255,0.35)",
        backgroundColor: mode === "modal" ? "transparent" : "rgba(255,255,255,0.88)",
        backdropFilter: mode === "modal" ? "none" : "blur(8px)",
        boxShadow: mode === "modal" ? "none" : "0 10px 30px rgba(2, 6, 23, 0.16)",
      }}
    >
      <Box sx={{ px: { xs: 2.2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle2" sx={{ ...textStyle, color: "#334155", fontWeight: 700, mb: 1 }}>
          Destination
        </Typography>
        <TextField
          label="City or country"
          variant="outlined"
          fullWidth
          size={mode === "inline" ? "small" : "medium"}
          sx={textStyle}
          value={searchData.location || ""}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: "#94a3b8" }} fontSize="small" />
              </InputAdornment>
            ),
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              queryData();
            }
          }}
          onChange={(e) =>
            setSearchData({
              ...searchData,
              location: e.target.value,
            })
          }
        />

        <Box
          sx={{
            mt: 2,
            p: { xs: 1.4, sm: 1.8 },
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography variant="subtitle2" sx={{ ...textStyle, color: "#334155", fontWeight: 700, mb: 1.2 }}>
            Preferred time
          </Typography>
          <AvailabilitySelector
            availabilityData={searchData?.availability}
            setAvailabilityData={(newData) =>
              setSearchData({
                ...searchData,
                availability: newData,
              })
            }
          />
          {searchData?.availability?.type && (
            <Button
              onClick={clearAvailability}
              sx={{ textTransform: "none", color: "#475569", px: 0, mt: 1, fontWeight: 600 }}
            >
              Clear time selection
            </Button>
          )}
        </Box>

        <Button
          onClick={() => setCollapseOpen(!collapseOpen)}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            color: "#0f172a",
            minWidth: "auto",
            padding: 0,
            mt: 1.8,
            fontWeight: 700,
          }}
        >
          <ArrowDropDownIcon
            sx={{
              color: "#0f172a",
              transform: collapseOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
              fontSize: "1.7rem",
            }}
          />
          {collapseOpen ? "Hide advanced filters" : "Show advanced filters"}
        </Button>

        <Collapse in={collapseOpen}>
          <Box
            sx={{
              mt: 1,
              p: { xs: 1.4, sm: 1.8 },
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
            }}
          >
            <IncrementorButton
              header={"Minimum number of bedrooms"}
              value={searchData?.bedrooms || 0}
              onUpdate={(newValue) =>
                setSearchData({
                  ...searchData,
                  bedrooms: newValue,
                })
              }
            />
            <IncrementorButton
              header={"Minimum number of bathrooms"}
              value={searchData?.bathrooms || 0}
              onUpdate={(newValue) =>
                setSearchData({
                  ...searchData,
                  bathrooms: newValue,
                })
              }
            />
          </Box>
        </Collapse>
      </Box>

      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={1.2}
        sx={{
          px: { xs: 2.2, sm: 3 },
          py: { xs: 1.6, sm: 2 },
          borderTop: "1px solid #e2e8f0",
          backgroundColor: "#fcfcfd",
          borderBottomLeftRadius: mode === "inline" ? 12 : 0,
          borderBottomRightRadius: mode === "inline" ? 12 : 0,
        }}
      >
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Typography sx={{ ...textStyle, color: "#64748b", fontSize: "0.9rem" }}>
            {activeFiltersCount === 0
              ? "No filters selected"
              : `${activeFiltersCount} filter${activeFiltersCount > 1 ? "s" : ""} selected`}
          </Typography>
          {activeFiltersCount > 0 && (
            <Button
              onClick={clearFilters}
              sx={{ textTransform: "none", color: "#334155", fontWeight: 600, p: 0.3, minWidth: 0 }}
            >
              Reset
            </Button>
          )}
        </Stack>

        <Stack direction="row" gap={1} justifyContent="flex-end">
          {onCancel && mode === "modal" && (
            <SecondaryButton text={isDesktop ? "Cancel" : "Close"} onClick={onCancel} />
          )}
          <MainButton
            text={activeFiltersCount === 0 ? "Go anywhere" : "Find homes"}
            onClick={queryData}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
